import { VgApiResult, vgApiRequest } from "./vg-api.service";
import { ORDER_STATE } from "constants/bookingEnums";

/**
 * Invoices, and turning a booking into one.
 *
 * A booking is a service order; invoicing it creates an Org_Invoice_Table row and
 * an Org_InvoiceableItem_Table line pointing back at that order, and moves the
 * order to Invoiced. All of that is one API call - Create_Invoice_From_ServiceOrderID
 * - so the money and the state change stay together rather than being assembled
 * from three round trips that can half-fail.
 *
 * That endpoint was broken until OlaCore 2026.8.226.1639: it created the invoice
 * with no organisation and no customer, and marked the order Invoiced first, so a
 * failure left a booking claiming to be billed with no invoice behind it. Anything
 * calling this needs at least that build.
 */

/** Mirrors ENUM_SYS_Invoice_State. 1-based, like every Core enum here. */
export const INVOICE_STATE = {
  UNKNOWN: 1,
  NOT_ISSUED: 2,
  ISSUED: 3,
  CLOSED: 4,
} as const;

export const getInvoiceStateLabel = (value: number | undefined) =>
  ({
    [INVOICE_STATE.NOT_ISSUED]: "Draft",
    [INVOICE_STATE.ISSUED]: "Issued",
    [INVOICE_STATE.CLOSED]: "Paid",
  }[value ?? INVOICE_STATE.UNKNOWN] ?? "Unknown");

export type InvoiceLine = {
  invoiceableItemId?: number;
  serviceOrderId?: number;
  /** Set when the line is a product rather than a service. */
  productId?: number;
  /** A custom charge's wording, from its service order metadata. */
  description?: string;
  amount?: number;
  currency?: number;
};

export type Invoice = {
  invoiceId: number;
  contactId?: number;
  contactFullName?: string;
  reference?: string;
  createdOn_UTC?: string;
  deadline_UTC?: string;
  invoiceState?: number;
  monetaryCurrency?: number;
  /** Total, amount settled, and what is still owed. */
  total?: number;
  amountPaid?: number;
  remainingBalance?: number;
  lines: InvoiceLine[];
  /** Rows against this invoice; the balance is computed from them. */
  payments: unknown[];
};

const num = (v: unknown) => (v != null ? Number(v) : undefined);
const str = (v: unknown) => (v != null ? String(v) : undefined);

/** The API answers in camelCase or PascalCase depending on the endpoint. */
const pick = (o: Record<string, unknown>, camel: string) =>
  o[camel] ?? o[camel.charAt(0).toUpperCase() + camel.slice(1)];

/** Money comes back as a complete object, not a scalar. */
function money(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number") return raw;
  const o = raw as Record<string, unknown>;
  return num(pick(o, "monetaryAmount") ?? pick(o, "amount") ?? pick(o, "value"));
}

function mapLine(raw: unknown): InvoiceLine {
  const o = raw as Record<string, unknown>;
  return {
    invoiceableItemId: num(pick(o, "invoiceableItemId")),
    serviceOrderId: num(pick(o, "serviceOrderId")),
    productId: num(pick(o, "productId")),
    description: lineDescriptionFromMetadata(o) ?? undefined,
    amount: money(pick(o, "computedMoneyValue") ?? pick(o, "monetaryAmount")),
    currency: num(pick(o, "monetaryCurrency")),
  };
}

export function mapInvoice(raw: unknown): Invoice {
  const o = (raw ?? {}) as Record<string, unknown>;
  const lines = pick(o, "listOfInvoiceableItems");
  return {
    invoiceId: Number(pick(o, "invoiceId") ?? 0),
    contactId: num(pick(o, "contactId")),
    contactFullName: str(pick(o, "contactFullName")),
    reference: str(pick(o, "reference")),
    createdOn_UTC: str(pick(o, "createdOn_UTC")),
    deadline_UTC: str(pick(o, "deadline_UTC")),
    invoiceState: num(pick(o, "invoiceState")),
    monetaryCurrency: num(pick(o, "monetaryCurrency")),
    total: money(pick(o, "computedMoneyValue")),
    amountPaid: money(pick(o, "amountPaid")),
    remainingBalance: money(pick(o, "remainingBalance")),
    lines: Array.isArray(lines) ? lines.map(mapLine) : [],
    payments: Array.isArray(pick(o, "paymentList")) ? (pick(o, "paymentList") as unknown[]) : [],
  };
}

type InvoiceListResponse = {
  listOfInvoices?: unknown[];
  ListOfInvoices?: unknown[];
};

/**
 * Raises an invoice for a booking. One call: the invoice, its line, and the
 * order's move to Invoiced.
 */
export async function createInvoiceFromBooking(
  accessToken: string,
  orgId: number,
  serviceOrderId: number
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_Invoice_From_ServiceOrderID", {
    orgId,
    serviceOrderId,
  });
}

export async function readOrgInvoices(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<Invoice[]>> {
  const { data, error } = await vgApiRequest<InvoiceListResponse>(
    accessToken,
    "Read_All_Organisation_Invoices_By_OrgID",
    { orgId }
  );
  if (error) return { data: null, error };
  const list = data?.listOfInvoices ?? data?.ListOfInvoices ?? [];
  return { data: list.map(mapInvoice), error: null };
}

/**
 * A patient's bills, which are really its owners' bills - an invoice is addressed
 * to a contact, not to an animal. Narrowed from the org read to the invoices whose
 * lines reference one of this patient's bookings, so what shows up is what this
 * animal was actually billed for rather than everything its owner owes.
 */
export async function readInvoicesForBookings(
  accessToken: string,
  orgId: number,
  serviceOrderIds: number[]
): Promise<VgApiResult<Invoice[]>> {
  if (serviceOrderIds.length === 0) return { data: [], error: null };

  const { data, error } = await readOrgInvoices(accessToken, orgId);
  if (error) return { data: null, error };

  const wanted = new Set(serviceOrderIds);
  const mine = (data ?? []).filter((invoice) =>
    invoice.lines.some((line) => line.serviceOrderId != null && wanted.has(line.serviceOrderId))
  );

  // Newest first: a bill people are chasing is the recent one.
  mine.sort((a, b) => (b.createdOn_UTC ?? "").localeCompare(a.createdOn_UTC ?? ""));
  return { data: mine, error: null };
}

/**
 * Adding lines to an invoice.
 *
 * An invoice line has no quantity column, and does not need one: three of
 * something is three lines. That keeps Org_InvoiceableItem_Table still - it is
 * shared with every other product built on OlaCore - and keeps each row meaning
 * one thing, which is what the table already says. Quantity is a fact about how a
 * bill reads, and groupLines below puts it back for display.
 *
 * The cost, stated plainly: selling a hundred of something writes a hundred rows.
 * Fine at clinic scale, and the day it is not, a quantity column is a migration
 * away and these rows still add up correctly.
 */

/** How many rows one call is willing to write. A typo should not cost 10,000 rows. */
export const MAX_LINE_QUANTITY = 100;

export type ProductLineInput = {
  orgId: number;
  invoiceId: number;
  productId: number;
  /** Price of one. Overridable, so a clinic can discount without editing the catalogue. */
  unitAmount: number;
  currency: number;
  quantity: number;
};

/**
 * Adds a product to an invoice, one row per unit.
 *
 * Sequential rather than parallel: these are writes to the same invoice, and a
 * half-applied burst of concurrent inserts is harder to reason about than a
 * sequence that stops where it failed. On failure it says how many landed,
 * because "3 of 5 were added" is something a user can act on and "failed" is not.
 */
export async function addProductLines(
  accessToken: string,
  input: ProductLineInput
): Promise<VgApiResult<{ added: number }>> {
  const quantity = Math.floor(input.quantity);

  if (!Number.isFinite(quantity) || quantity < 1) {
    return { data: null, error: { message: "Quantity must be at least 1." } };
  }
  if (quantity > MAX_LINE_QUANTITY) {
    return {
      data: null,
      error: { message: `Quantity is limited to ${MAX_LINE_QUANTITY} per line.` },
    };
  }

  for (let added = 0; added < quantity; added += 1) {
    const { error } = await vgApiRequest(accessToken, "Create_Org_InvoiceableItem", {
      orgId: input.orgId,
      invoiceId: input.invoiceId,
      productId: input.productId,
      serviceOrderId: 0,
      monetaryAmount: input.unitAmount,
      monetaryCurrency: input.currency,
    });

    if (error) {
      return {
        data: null,
        error: {
          ...error,
          message:
            added === 0
              ? error.message
              : `${error.message} (${added} of ${quantity} were added)`,
        },
      };
    }
  }

  return { data: { added: quantity }, error: null };
}

export type GroupedLine = {
  /** Stable within one invoice: what was sold, at what price. */
  key: string;
  productId?: number;
  serviceOrderId?: number;
  description: string;
  unitAmount?: number;
  currency?: number;
  quantity: number;
  total?: number;
  /** Every underlying row, so a caller can remove them all. */
  invoiceableItemIds: number[];
};

/**
 * Turns rows back into lines a person would recognise: three identical rows
 * become one line of quantity 3.
 *
 * Grouped by what was sold AND what it cost, so the same product billed at two
 * different prices stays two lines. Merging those would show one quantity against
 * one of the prices, which misstates the bill.
 *
 * Lines backed by a service order are never grouped: each booking is its own
 * appointment even when two look identical on paper.
 */
export function groupLines(
  lines: InvoiceLine[],
  describe?: (line: InvoiceLine) => string
): GroupedLine[] {
  const label = (line: InvoiceLine) =>
    describe?.(line) ??
    (line.productId
      ? `Product ${line.productId}`
      : line.serviceOrderId
        ? `Booking ${line.serviceOrderId}`
        : "Item");

  const grouped: GroupedLine[] = [];
  const byKey = new Map<string, GroupedLine>();

  lines.forEach((line, index) => {
    const groupable =
      !line.serviceOrderId && line.productId != null && line.productId > 0;
    const key = groupable
      ? `p:${line.productId}:${line.amount ?? ""}:${line.currency ?? ""}`
      : `i:${line.invoiceableItemId ?? index}`;

    const existing = groupable ? byKey.get(key) : undefined;
    if (existing) {
      existing.quantity += 1;
      existing.total =
        existing.unitAmount != null
          ? existing.unitAmount * existing.quantity
          : undefined;
      if (line.invoiceableItemId != null) {
        existing.invoiceableItemIds.push(line.invoiceableItemId);
      }
      return;
    }

    const group: GroupedLine = {
      key,
      productId: line.productId,
      serviceOrderId: line.serviceOrderId,
      description: label(line),
      unitAmount: line.amount,
      currency: line.currency,
      quantity: 1,
      total: line.amount,
      invoiceableItemIds:
        line.invoiceableItemId != null ? [line.invoiceableItemId] : [],
    };
    grouped.push(group);
    if (groupable) byKey.set(key, group);
  });

  return grouped;
}

/** One invoice, with its lines. Backs the invoice screen. */
export async function readInvoice(
  accessToken: string,
  orgId: number,
  invoiceId: number
): Promise<VgApiResult<Invoice>> {
  const { data, error } = await vgApiRequest<Record<string, unknown>>(
    accessToken,
    "Read_Invoice_By_InvoiceID",
    { orgId, invoiceId }
  );
  if (error) return { data: null, error };
  return { data: mapInvoice(data), error: null };
}

/**
 * Removes a line. A grouped line is several rows, so removing "3 of something"
 * means three deletes; the caller passes every id in the group.
 *
 * Sequential and reporting partial progress, for the same reason adding is.
 */
export async function removeLines(
  accessToken: string,
  orgId: number,
  invoiceableItemIds: number[]
): Promise<VgApiResult<{ removed: number }>> {
  for (let removed = 0; removed < invoiceableItemIds.length; removed += 1) {
    const { error } = await vgApiRequest(accessToken, "Delete_Org_InvoiceableItem", {
      orgId,
      invoiceableItemId: invoiceableItemIds[removed],
    });
    if (error) {
      return {
        data: null,
        error: {
          ...error,
          message:
            removed === 0
              ? error.message
              : `${error.message} (${removed} of ${invoiceableItemIds.length} were removed)`,
        },
      };
    }
  }
  return { data: { removed: invoiceableItemIds.length }, error: null };
}

/**
 * The invoice as a PDF, produced by OlaCore rather than by the browser.
 *
 * The document is built server-side (SC_Export.CreateInvoicePDF, on MigraDoc)
 * because the layout of an invoice is the same problem for every product on the
 * library, and because a PDF the server made is one the server can also attach to
 * an email. Printing the screen would have given a different document to whatever
 * gets sent later, which is the sort of difference nobody notices until a client
 * queries a bill.
 *
 * Returns the raw bytes as a Blob, ready to download.
 */
export async function exportInvoicePdf(
  accessToken: string,
  orgId: number,
  invoiceId: number
): Promise<VgApiResult<Blob>> {
  const { data, error } = await vgApiRequest<{ byteArrayData?: string; ByteArrayData?: string }>(
    accessToken,
    "Export_Invoice_PDF",
    { orgId, invoiceId }
  );
  if (error) return { data: null, error };

  const encoded = data?.byteArrayData ?? data?.ByteArrayData;
  if (!encoded) {
    return { data: null, error: { message: "The invoice PDF came back empty." } };
  }

  try {
    // The API sends the bytes base64-encoded in JSON.
    const binary = window.atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return { data: new Blob([bytes], { type: "application/pdf" }), error: null };
  } catch {
    return { data: null, error: { message: "The invoice PDF could not be decoded." } };
  }
}

/**
 * A custom charge: a callout fee, a dressing, an agreed discount. Something that
 * is neither a booking nor a catalogue product, and still has to appear on the
 * bill with words next to the number.
 *
 * There is no description column on an invoice line, and adding one would move
 * the schema for every product built on OlaCore. Instead the line points at a
 * service order against a platform service, and that order carries its
 * description as metadata - keyed by identifier and group, so it is a seek rather
 * than a scan, and hydrated for a whole page in one query by the org read.
 *
 * The awkward part, stated rather than hidden: a service order wants a start and
 * an end, and a callout fee has neither. Both are set to now. It is a booking
 * shaped like a charge, which is the price of not changing the schema.
 */
const LINE_DESCRIPTION_IDENTIFIER = "lineDescription";
const LINE_METADATA_GROUP = "invoiceLine";

/** Enum_MetaDataType.Custom, matching bookings.service. These enums start at 1. */
const METADATA_TYPE_CUSTOM = 4;

export type CustomLineInput = {
  orgId: number;
  invoiceId: number;
  contactId: number;
  /** The platform service and configuration the charge hangs off. */
  serviceId: number;
  serviceFulfilmentConfigId: number;
  description: string;
  amount: number;
  currency: number;
};

/**
 * Adds a custom charge. Two writes: the service order that carries the words,
 * then the invoice line that carries the money.
 *
 * If the second fails the order is left behind - an order with no invoice line,
 * which shows up nowhere and bills nobody. That is the safer of the two possible
 * leftovers; the other way round would be a charge on a bill with no explanation.
 */
export async function addCustomLine(
  accessToken: string,
  input: CustomLineInput
): Promise<VgApiResult<{ serviceOrderId: number }>> {
  const description = input.description.trim();

  if (!description) {
    return {
      data: null,
      error: { message: "A custom charge needs a description." },
    };
  }
  if (!Number.isFinite(input.amount)) {
    return { data: null, error: { message: "Enter an amount." } };
  }

  const now = new Date().toISOString();

  const { data, error } = await vgApiRequest<{
    newRecordID?: number;
    NewRecordID?: number;
  }>(accessToken, "Create_ServiceOrder", {
    orgId: input.orgId,
    contactId: input.contactId,
    serviceId: input.serviceId,
    serviceFulfilmentConfigId: input.serviceFulfilmentConfigId,
    start: now,
    end: now,
    orderState: ORDER_STATE.INVOICED,
    monetaryAmount: input.amount,
    monetaryCurrency: input.currency,
    metadataOptionList: [
      {
        type: METADATA_TYPE_CUSTOM,
        metaData: description,
        metaDataIdentifier: LINE_DESCRIPTION_IDENTIFIER,
        metaDataGroupIdentifier: LINE_METADATA_GROUP,
      },
    ],
  });

  if (error) return { data: null, error };

  const serviceOrderId = Number(data?.newRecordID ?? data?.NewRecordID ?? 0);
  if (!serviceOrderId) {
    return {
      data: null,
      error: { message: "The charge was not created." },
    };
  }

  const line = await vgApiRequest(accessToken, "Create_Org_InvoiceableItem", {
    orgId: input.orgId,
    invoiceId: input.invoiceId,
    productId: 0,
    serviceOrderId,
    monetaryAmount: input.amount,
    monetaryCurrency: input.currency,
  });

  if (line.error) return { data: null, error: line.error };
  return { data: { serviceOrderId }, error: null };
}

/**
 * Pulls a custom charge's wording back out of its service order metadata.
 * Undefined for an ordinary booking, which has no such metadata.
 */
export function lineDescriptionFromMetadata(raw: unknown): string | undefined {
  const list = (raw as Record<string, unknown>)?.metadataOptionList;
  if (!Array.isArray(list)) return undefined;
  for (const entry of list) {
    const m = entry as Record<string, unknown>;
    const identifier = String(m.metaDataIdentifier ?? m.MetaDataIdentifier ?? "");
    if (identifier === LINE_DESCRIPTION_IDENTIFIER) {
      const value = String(m.metaData ?? m.MetaData ?? "").trim();
      return value || undefined;
    }
  }
  return undefined;
}
